import React, { lazy, Suspense, useState } from "react";
import AddHomeIcon from "@mui/icons-material/AddHome";
import ConstructionIcon from "@mui/icons-material/Construction";

import getScoreColor from "helpers/scoreColor";
import { useWaitlist } from "contexts/waitlist";
import "./hero.scss";

const MapBackground = lazy(() => import("components/MapBackground"));

const MOCK_DATA = {
  address: "1847 Oakwood Dr, Decatur, GA",
  beds: 3,
  baths: 2,
  sqft: "1,840",
  yearBuilt: 1972,
  ownership: "Original owner",
  score: 81,
  arv: "$295,000",
  askingPrice: "$185,000",
  rehab: "$42,000",
  profit: "$32,100",
  roi: "29.4%",
  holdTime: "5.8 mo",
  strategy: "Fix & Flip",
  riskLevel: "Moderate",
  comps: 8,
  daysOnMarket: 23,
  lendersMatched: 4,
  topRate: "8.15%",
};

const SCORE_FACTORS = [
  { label: "Nearby sales", value: 88 },
  { label: "Market", value: 74 },
  { label: "Rehab", value: 82 },
  { label: "Resale risk", value: 79 },
];

// Property tags shown in the mockup card — check = opportunity, x = cost/risk
const PROPERTY_TAGS = [
  {
    label: "Rental unit possible",
    pass: true,
    icon: <AddHomeIcon sx={{ fontSize: 14 }} />,
  },
  {
    label: "Demolition needed",
    pass: false,
    icon: <ConstructionIcon sx={{ fontSize: 14 }} />,
  },
];

const getScoreLabel = (score) => {
  if (score >= 80) return "Strong Buy";
  if (score >= 60) return "Viable";
  if (score >= 40) return "Risky";
  return "Pass";
};

const Hero = () => {
  const [email, setEmail] = useState("");
  const {
    state: { status },
    submit,
    openModal,
  } = useWaitlist();

  const handleSubmit = (e) => {
    e.preventDefault();
    submit(email);
  };

  const scoreColor = getScoreColor(MOCK_DATA.score);

  return (
    <section className="hero">
      <Suspense
        fallback={
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(135deg, #0c1322, #111827, #0f172a)",
            }}
          />
        }
      >
        <MapBackground />
      </Suspense>
      <div className="hero-inner">
        <div className="hero-content">
          <h1 className="hero-headline">
            Flip smarter.
            <br />
            <span className="gradient-text">Not harder.</span>
          </h1>
          <p className="hero-sub">
            NextFlip scores every property using MLS, county, permit, and lender
            data — then connects you with financing and contractors so you can
            move faster.
          </p>
          {status === "success" ? (
            <div className="hero-confirmed">
              <span>You're on the list. We'll be in touch.</span>
            </div>
          ) : (
            <form className="hero-form" onSubmit={handleSubmit}>
              <input
                type="email"
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                aria-label="Email address"
              />
              <button type="submit" disabled={status === "loading"}>
                {status === "loading" ? "Submitting\u2026" : "Get Early Access"}
              </button>
            </form>
          )}
          {status === "error" && (
            <span className="hero-error">
              Something went wrong. Please try again.
            </span>
          )}
          <span className="hero-note">
            Free during beta. No credit card required.
          </span>
        </div>
        <div className="hero-mockup">
          <div className="mockup-card">
            <div className="mockup-header">
              <div className="mockup-badges">
                <span className="mockup-badge">{MOCK_DATA.strategy}</span>
                <span className="mockup-badge score-badge">
                  {getScoreLabel(MOCK_DATA.score)}
                </span>
              </div>
              <span className="mockup-address">{MOCK_DATA.address}</span>
              <span className="mockup-details">
                {MOCK_DATA.beds}bd / {MOCK_DATA.baths}ba &middot;{" "}
                {MOCK_DATA.sqft} sqft &middot; Built {MOCK_DATA.yearBuilt}
                &middot; {MOCK_DATA.ownership}
              </span>
              <span className="mockup-dom">
                {MOCK_DATA.daysOnMarket} days on market &middot; Found by
                NextFlip AI
              </span>
            </div>

            <div className="mockup-score-row">
              <div className="score-ring">
                <svg viewBox="0 0 100 100">
                  <circle className="score-track" cx="50" cy="50" r="42" />
                  <circle
                    className="score-fill"
                    cx="50"
                    cy="50"
                    r="42"
                    stroke={scoreColor}
                    strokeDasharray={`${MOCK_DATA.score * 2.64} 264`}
                  />
                </svg>
                <span className="score-value" style={{ color: scoreColor }}>
                  {MOCK_DATA.score}
                </span>
              </div>
              <div className="score-details">
                <span className="score-details-label">Deal Score</span>
                <span className="score-details-sub">
                  {MOCK_DATA.comps} comparable sales within 0.5 mi
                </span>
                <span className="score-risk">Risk: {MOCK_DATA.riskLevel}</span>
              </div>
            </div>

            <div className="score-factors">
              {SCORE_FACTORS.map((factor) => {
                const barColor = getScoreColor(factor.value);
                return (
                  <div key={factor.label} className="score-factor">
                    <span className="score-factor-label">{factor.label}</span>
                    <div className="score-factor-bar">
                      <div
                        className="score-factor-fill"
                        style={{
                          width: `${factor.value}%`,
                          background: barColor,
                        }}
                      />
                    </div>
                    <span
                      className="score-factor-value"
                      style={{ color: barColor }}
                    >
                      {factor.value}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="mockup-divider" />

            <div className="mockup-metrics">
              <div className="metric">
                <span className="metric-label">Asking Price</span>
                <span className="metric-value">{MOCK_DATA.askingPrice}</span>
              </div>
              <div className="metric">
                <span className="metric-label">
                  After-Repair Value
                  <span className="metric-hint">
                    what it sells for fixed up
                  </span>
                </span>
                <span className="metric-value">{MOCK_DATA.arv}</span>
              </div>
              <div className="metric">
                <span className="metric-label">Est. Rehab</span>
                <span className="metric-value">{MOCK_DATA.rehab}</span>
              </div>
              <div className="metric highlight">
                <span className="metric-label">
                  Net Profit
                  <span className="metric-hint">
                    after closing, holding &amp; financing
                  </span>
                </span>
                <span className="metric-value profit">{MOCK_DATA.profit}</span>
              </div>
            </div>

            {/* Property tags — check/x with icons */}
            <div className="property-tags">
              {PROPERTY_TAGS.map((tag) => (
                <div
                  key={tag.label}
                  className={`property-tag ${tag.pass ? "pass" : "fail"}`}
                >
                  <span className="property-tag-icon">{tag.icon}</span>
                  <span className="property-tag-label">{tag.label}</span>
                  <span className="property-tag-status">
                    {tag.pass ? (
                      <svg
                        viewBox="0 0 12 12"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      >
                        <path d="M2.5 6l2.5 2.5 4.5-5" />
                      </svg>
                    ) : (
                      <svg
                        viewBox="0 0 12 12"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      >
                        <path d="M3 3l6 6M9 3l-6 6" />
                      </svg>
                    )}
                  </span>
                </div>
              ))}
            </div>

            <div className="mockup-bottom-row">
              <div className="bottom-stat">
                <span className="bottom-value">{MOCK_DATA.roi}</span>
                <span className="bottom-label">ROI</span>
              </div>
              <div className="bottom-stat">
                <span className="bottom-value">{MOCK_DATA.holdTime}</span>
                <span className="bottom-label">Hold</span>
              </div>
              <div className="bottom-stat">
                <span className="bottom-value">{MOCK_DATA.lendersMatched}</span>
                <span className="bottom-label">Lenders</span>
              </div>
              <div className="bottom-stat">
                <span className="bottom-value">{MOCK_DATA.topRate}</span>
                <span className="bottom-label">Best Rate</span>
              </div>
            </div>

            <button className="mockup-cta" onClick={openModal}>
              Get Early Access
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
